import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import * as XLSX from 'xlsx';
import './BulkShipping.css';
import { generateCommercialInvoiceHTML } from './CommercialInvoice';

const BULK_COLUMNS = [
    'ShipNumber',
    'AccountNO',
    'Shippercontactname',
    'shippercompany',
    'Shippercountry',
    'ShipperAddressone',
    'ShipperAddresstwo',
    'ShipperCity',
    'ShipperPostalCode',
    'ShipperState',
    'ShipperTELE',
    'ShipperEmail',
    'ShipperTaxID',
    'ContactName',
    'Companyname',
    'Country',
    'Addressone',
    'Addresstwo',
    'Addressthree',
    'City',
    'StateProvOther',
    'PostalCode',
    'Telephone',
    'Ext',
    'ResidentialInd',
    'ConsigneeEmail',
    'PackagingType',
    'CustomsValue',
    'Weight',
    'Length',
    'Width',
    'Height',
    'UnitofMeasure',
    'DescriptionofGoods',
    'OriginCountry',
    'TariffCode',
    'PartNumber',
    'NumberOfUnits',
    'UnitPrice',
    'UnitPriceCurrency',
    'InVoiceTaxPaid',
    'UPC',
    'ASIN',
    'DocumentsofNoCommercialValue',
    'GNIFC',
    'PkgDeclValue',
    'Service',
    'DeliveryConfirm',
    'ShipperRelease',
    'RetofDocuments',
    'SaturdayDeliver',
    'CarbonNeutral',
    'LargePackage',
    'Addlhandling',
    'Referenceone',
    'Referencetwo',
    'Referencethree'
];

const EDIT_SECTIONS = [
    {
        id: 'account',
        title: 'Shipment & Account',
        icon: '📋',
        fields: [
            { key: 'ShipNumber', label: 'Shipment Number / Ref ID', placeholder: 'e.g. SHIP-001' },
            { key: 'AccountNO', label: 'UPS Account Number', placeholder: 'e.g. 89R907', required: true, isAccount: true },
            {
                key: 'Service',
                label: 'UPS Service Code',
                type: 'select',
                options: [
                    { value: '65', label: '65 - UPS Express Saver' },
                    { value: '07', label: '07 - UPS Express' },
                    { value: '11', label: '11 - UPS Standard' },
                    { value: '08', label: '08 - UPS Worldwide Expedited' },
                    { value: '54', label: '54 - UPS Worldwide Express Plus' }
                ]
            }
        ]
    },
    {
        id: 'shipper',
        title: 'Shipper (Sender) Information',
        icon: '🏢',
        fields: [
            { key: 'Shippercontactname', label: 'Shipper Contact Name', placeholder: 'Contact person', required: true },
            { key: 'shippercompany', label: 'Shipper Company Name', placeholder: 'Company name', required: true },
            { key: 'Shippercountry', label: 'Shipper Country (2-letter ISO)', placeholder: 'PK', required: true },
            { key: 'ShipperAddressone', label: 'Shipper Street Address 1', placeholder: 'Street address line 1', required: true },
            { key: 'ShipperAddresstwo', label: 'Shipper Address Line 2', placeholder: 'Suite, floor, etc.' },
            { key: 'ShipperCity', label: 'Shipper City', placeholder: 'City', required: true },
            { key: 'ShipperState', label: 'Shipper State / Province', placeholder: 'State or Province' },
            { key: 'ShipperPostalCode', label: 'Shipper Postal / ZIP Code', placeholder: 'Postal code' },
            { key: 'ShipperTELE', label: 'Shipper Telephone', placeholder: '+923001234567', required: true },
            { key: 'ShipperEmail', label: 'Shipper Email', placeholder: 'shipper@example.com' },
            { key: 'ShipperTaxID', label: 'Shipper Tax ID / NTN', placeholder: 'Tax ID' }
        ]
    },
    {
        id: 'consignee',
        title: 'Consignee (Recipient) Details',
        icon: '📍',
        fields: [
            { key: 'ContactName', label: 'Recipient Contact Name', placeholder: 'Full Name', required: true },
            { key: 'Companyname', label: 'Recipient Company Name', placeholder: 'Company Name' },
            { key: 'Country', label: 'Destination Country (2-letter ISO)', placeholder: 'US, GB, CA, AE', required: true },
            { key: 'Addressone', label: 'Delivery Address Line 1', placeholder: 'Street address line 1', required: true },
            { key: 'Addresstwo', label: 'Delivery Address Line 2', placeholder: 'Suite, apt, etc.' },
            { key: 'Addressthree', label: 'Delivery Address Line 3', placeholder: 'Additional address info' },
            { key: 'City', label: 'Destination City', placeholder: 'City name', required: true },
            { key: 'StateProvOther', label: 'State / Province (Required for US/CA)', placeholder: 'NY, CA, ON', required: false },
            { key: 'PostalCode', label: 'Postal / ZIP Code', placeholder: 'ZIP / Postal code', required: true },
            { key: 'Telephone', label: 'Recipient Telephone', placeholder: '+12125551234', required: true },
            { key: 'Ext', label: 'Phone Extension', placeholder: 'Ext' },
            {
                key: 'ResidentialInd',
                label: 'Residential Address?',
                type: 'select',
                options: [
                    { value: 'N', label: 'N - Commercial / Business Address' },
                    { value: 'Y', label: 'Y - Residential / Home Address' }
                ]
            },
            { key: 'ConsigneeEmail', label: 'Recipient Email', placeholder: 'consignee@example.com' }
        ]
    },
    {
        id: 'package',
        title: 'Package & Dimensions',
        icon: '📦',
        fields: [
            {
                key: 'PackagingType',
                label: 'Packaging Type',
                type: 'select',
                options: [
                    { value: '02', label: '02 - Customer Supplied Box' },
                    { value: '01', label: '01 - UPS Letter / Envelope' },
                    { value: '03', label: '03 - UPS Tube' },
                    { value: '04', label: '04 - UPS Pak' },
                    { value: '21', label: '21 - UPS Express Box' },
                    { value: '24', label: '24 - UPS 25kg Box' },
                    { value: '25', label: '25 - UPS 10kg Box' }
                ]
            },
            { key: 'Weight', label: 'Package Weight', type: 'number', step: '0.1', placeholder: 'e.g. 2.5', required: true },
            {
                key: 'UnitofMeasure',
                label: 'Weight & Dim Units',
                type: 'select',
                options: [
                    { value: 'KGS', label: 'KGS / CM (Metric)' },
                    { value: 'LBS', label: 'LBS / IN (Imperial)' }
                ]
            },
            { key: 'Length', label: 'Length', type: 'number', placeholder: '25' },
            { key: 'Width', label: 'Width', type: 'number', placeholder: '20' },
            { key: 'Height', label: 'Height', type: 'number', placeholder: '15' },
            { key: 'CustomsValue', label: 'Declared Customs Total ($)', type: 'number', step: '0.01', placeholder: '100.00' },
            { key: 'PkgDeclValue', label: 'Package Declared Insurance Value ($)', type: 'number', step: '0.01', placeholder: '100.00' },
            {
                key: 'LargePackage',
                label: 'Large Package Indicator',
                type: 'select',
                options: [
                    { value: 'N', label: 'N - Standard Package' },
                    { value: 'Y', label: 'Y - Large Package' }
                ]
            },
            {
                key: 'Addlhandling',
                label: 'Additional Handling Required',
                type: 'select',
                options: [
                    { value: 'N', label: 'N - No' },
                    { value: 'Y', label: 'Y - Yes (Extra handling)' }
                ]
            }
        ]
    },
    {
        id: 'customs',
        title: 'Commercial Invoice & Customs Line Items',
        icon: '📑',
        fields: [
            { key: 'DescriptionofGoods', label: 'Product / Merchandise Description', placeholder: 'e.g. Leather Handbags', required: true },
            { key: 'TariffCode', label: 'Harmonized Tariff / HS Code', placeholder: 'e.g. 4202.21', required: true },
            { key: 'OriginCountry', label: 'Country of Origin (2-letter ISO)', placeholder: 'PK', required: true },
            { key: 'NumberOfUnits', label: 'Quantity / Units', type: 'number', placeholder: '1', required: true },
            { key: 'UnitPrice', label: 'Unit Price', type: 'number', step: '0.01', placeholder: '50.00', required: true },
            {
                key: 'UnitPriceCurrency',
                label: 'Invoice Currency',
                type: 'select',
                options: [
                    { value: 'USD', label: 'USD - US Dollar' },
                    { value: 'EUR', label: 'EUR - Euro' },
                    { value: 'GBP', label: 'GBP - British Pound' },
                    { value: 'CAD', label: 'CAD - Canadian Dollar' },
                    { value: 'AED', label: 'AED - UAE Dirham' },
                    { value: 'PKR', label: 'PKR - Pakistani Rupee' }
                ]
            },
            {
                key: 'InVoiceTaxPaid',
                label: 'Invoice Tax Paid?',
                type: 'select',
                options: [
                    { value: 'N', label: 'N - No' },
                    { value: 'Y', label: 'Y - Yes' }
                ]
            },
            { key: 'PartNumber', label: 'Part / SKU Number', placeholder: 'SKU-001' },
            { key: 'UPC', label: 'UPC Barcode', placeholder: 'UPC code' },
            { key: 'ASIN', label: 'Amazon ASIN', placeholder: 'ASIN' },
            {
                key: 'DocumentsofNoCommercialValue',
                label: 'Documents with No Commercial Value?',
                type: 'select',
                options: [
                    { value: 'N', label: 'N - Merchandise (With Commercial Invoice)' },
                    { value: 'Y', label: 'Y - Documents Only (No Commercial Invoice)' }
                ]
            },
            {
                key: 'GNIFC',
                label: 'Goods Not In Free Circulation (GNIFC)',
                type: 'select',
                options: [
                    { value: 'N', label: 'N - No' },
                    { value: 'Y', label: 'Y - Yes' }
                ]
            }
        ]
    },
    {
        id: 'options',
        title: 'Delivery Options & References',
        icon: '⚙️',
        fields: [
            {
                key: 'DeliveryConfirm',
                label: 'Delivery Confirmation / Signature',
                type: 'select',
                options: [
                    { value: '', label: 'None (Standard Delivery)' },
                    { value: 'Signature', label: 'Signature Required' },
                    { value: 'AdultSignature', label: 'Adult Signature Required' }
                ]
            },
            {
                key: 'SaturdayDeliver',
                label: 'Saturday Delivery',
                type: 'select',
                options: [
                    { value: 'N', label: 'N - No' },
                    { value: 'Y', label: 'Y - Yes' }
                ]
            },
            {
                key: 'CarbonNeutral',
                label: 'UPS Carbon Neutral',
                type: 'select',
                options: [
                    { value: 'N', label: 'N - No' },
                    { value: 'Y', label: 'Y - Yes' }
                ]
            },
            {
                key: 'ShipperRelease',
                label: 'Shipper Release',
                type: 'select',
                options: [
                    { value: 'N', label: 'N - No' },
                    { value: 'Y', label: 'Y - Yes' }
                ]
            },
            {
                key: 'RetofDocuments',
                label: 'Return of Documents',
                type: 'select',
                options: [
                    { value: 'N', label: 'N - No' },
                    { value: 'Y', label: 'Y - Yes' }
                ]
            },
            { key: 'Referenceone', label: 'Reference Number 1', placeholder: 'PO number, customer ref' },
            { key: 'Referencetwo', label: 'Reference Number 2', placeholder: 'Department, job number' },
            { key: 'Referencethree', label: 'Reference Number 3', placeholder: 'Internal code' }
        ]
    }
];

const BulkShipping = ({
    onBack,
    userModules = [],
    userAccounts = [],
    isSuperUser = false,
    currentUser = '',
    userCity = '',
    companySlug = '',
    companyName = ''
}) => {
    const fileInputRef = useRef(null);
    const [activeTab, setActiveTab] = useState('upload'); // 'upload' | 'history' | 'guide'
    const [fileName, setFileName] = useState('');
    const [isDragging, setIsDragging] = useState(false);
    const [parsedRows, setParsedRows] = useState([]);
    const [defaultAccount, setDefaultAccount] = useState('');
    const [accountsList, setAccountsList] = useState([]);
    const [filterStatus, setFilterStatus] = useState('all'); // 'all' | 'ready' | 'error' | 'success'

    // Processing & Progress state
    const [isProcessing, setIsProcessing] = useState(false);
    const [processProgress, setProcessProgress] = useState(0); // 0 - 100
    const [processedCount, setProcessedCount] = useState(0);
    const [totalToProcess, setTotalToProcess] = useState(0);
    const [liveLogs, setLiveLogs] = useState([]);
    const [cancelRequested, setCancelRequested] = useState(false);

    // Modals
    const [labelModalData, setLabelModalData] = useState(null); // array of base64
    const [ciModalData, setCiModalData] = useState(null); // html string
    const [editRowModal, setEditRowModal] = useState(null); // { index, data, row }
    const [editActiveSection, setEditActiveSection] = useState('all');
    const [editSearchQuery, setEditSearchQuery] = useState('');

    // History
    const [historyList, setHistoryList] = useState([]);
    const [loadingHistory, setLoadingHistory] = useState(false);

    const API_BASE = process.env.NODE_ENV === 'development' ? 'http://localhost:5001/api' : '/api';
    const hasAllAccess = isSuperUser || (Array.isArray(userAccounts) && userAccounts.includes('all'));

    // Fetch accounts accurately from Company details, tariffs, and user permissions
    const fetchAvailableAccounts = useCallback(async () => {
        try {
            const fetched = [];

            // 1. If company context, load company profile for upsAccountNumber and assigned accounts
            if (companySlug) {
                try {
                    const cRes = await axios.get(`${API_BASE}/companies/${companySlug}`);
                    if (cRes.data?.success && cRes.data?.company) {
                        const comp = cRes.data.company;
                        if (comp.upsAccountNumber && String(comp.upsAccountNumber).trim() && String(comp.upsAccountNumber).toLowerCase() !== 'all') {
                            fetched.push(String(comp.upsAccountNumber).trim());
                        }
                        if (Array.isArray(comp.accounts)) {
                            comp.accounts.forEach(a => {
                                if (a && String(a).trim() && String(a).toLowerCase() !== 'all') {
                                    fetched.push(String(a).trim());
                                }
                            });
                        }
                    }
                } catch (e) {
                    console.warn('[BulkShipping] Could not load company accounts:', e.message);
                }
            }

            // 2. Load tariffs accounts (company tariffs or global tariffs)
            const tariffEndpoint = companySlug
                ? `${API_BASE}/companies/${companySlug}/tariffs`
                : `${API_BASE}/settings/tariffs`;
            try {
                const tRes = await axios.get(tariffEndpoint);
                if (tRes.data?.success && Array.isArray(tRes.data?.tariffs)) {
                    tRes.data.tariffs.forEach(t => {
                        if (t.account && String(t.account).trim() && String(t.account).toLowerCase() !== 'all') {
                            fetched.push(String(t.account).trim());
                        }
                    });
                }
            } catch (e) {
                console.warn('[BulkShipping] Could not load tariffs:', e.message);
            }

            // 3. User accounts prop from profile (excluding literal 'all')
            if (Array.isArray(userAccounts)) {
                userAccounts.forEach(a => {
                    if (a && typeof a === 'string' && a.trim() && a.toLowerCase() !== 'all') {
                        fetched.push(a.trim());
                    }
                });
            }

            // 4. Fallback to global tariffs if company has none yet
            if (fetched.length === 0) {
                try {
                    const gRes = await axios.get(`${API_BASE}/settings/tariffs`);
                    if (gRes.data?.success && Array.isArray(gRes.data?.tariffs)) {
                        gRes.data.tariffs.forEach(t => {
                            if (t.account && String(t.account).trim() && String(t.account).toLowerCase() !== 'all') {
                                fetched.push(String(t.account).trim());
                            }
                        });
                    }
                } catch (e) { }
            }

            // Deduplicate real accounts
            const unique = [...new Set(fetched.map(a => a.toUpperCase()))].filter(a => a && a !== 'ALL');

            let finalAccounts = unique;
            if (!hasAllAccess && Array.isArray(userAccounts) && userAccounts.filter(a => a !== 'all').length > 0) {
                const allowed = userAccounts.map(a => a.toUpperCase());
                finalAccounts = unique.filter(a => allowed.includes(a));
                userAccounts.forEach(a => {
                    if (a && a !== 'all' && !finalAccounts.includes(a.toUpperCase())) {
                        finalAccounts.push(a.toUpperCase());
                    }
                });
            }

            if (finalAccounts.length > 0) {
                setAccountsList(finalAccounts);
                setDefaultAccount(prev => {
                    if (prev && prev.toLowerCase() !== 'all' && finalAccounts.includes(prev.toUpperCase())) {
                        return prev.toUpperCase();
                    }
                    return finalAccounts[0];
                });
            } else if (companySlug) {
                // If company without tariffs, fallback to UPS account from environment
                setAccountsList(['89R907']);
                setDefaultAccount('89R907');
            }
        } catch (err) {
            console.error('[BulkShipping] Error resolving accounts:', err);
        }
    }, [companySlug, userAccounts, hasAllAccess, API_BASE]);

    useEffect(() => {
        fetchAvailableAccounts();
    }, [fetchAvailableAccounts]);

    // Validation helper
    const validateRow = useCallback((rowObj, accList, defAcc) => {
        const rawAccount = String(rowObj.AccountNO || '').trim();
        // If rawAccount is 'all' or empty, use defaultAccount or fallback
        let effectiveAccount = rawAccount;
        if (!effectiveAccount || effectiveAccount.toLowerCase() === 'all') {
            effectiveAccount = defAcc || (accList.length > 0 ? accList[0] : '');
        }
        rowObj.effectiveAccount = effectiveAccount;

        const errors = [];
        if (!effectiveAccount || effectiveAccount.toLowerCase() === 'all') {
            errors.push('Account number missing (select a Default UPS Account or enter in sheet)');
        } else if (!hasAllAccess && accList.length > 0 && !accList.map(a => String(a).toUpperCase()).includes(effectiveAccount.toUpperCase())) {
            errors.push(`Account ${effectiveAccount} not authorized for your user`);
        }

        if (!rowObj.Country) errors.push('Destination Country is missing');
        if (!rowObj.Addressone) errors.push('Destination Address is missing');
        if (!rowObj.City) errors.push('Destination City is missing');
        if (!rowObj.ContactName && !rowObj.Companyname) errors.push('Recipient Name is missing');
        if (!rowObj.Weight || parseFloat(rowObj.Weight) <= 0) errors.push('Weight must be greater than 0');

        const country = String(rowObj.Country || '').trim().toUpperCase();
        if (['US', 'CA'].includes(country)) {
            const state = String(rowObj.StateProvOther || '').trim();
            if (!state || state.length !== 2) {
                errors.push(`State code (2 chars) required for ${country}`);
            }
        }

        return {
            rowObj,
            errors,
            status: errors.length > 0 ? 'error' : 'ready'
        };
    }, [hasAllAccess]);

    // Re-validate parsed rows if defaultAccount or accountsList changes
    useEffect(() => {
        if (parsedRows.length > 0 && defaultAccount) {
            setParsedRows(prev => prev.map(r => {
                if (r.status === 'success' || r.status === 'processing') return r;
                const v = validateRow({ ...r.data }, accountsList, defaultAccount);
                return {
                    ...r,
                    data: v.rowObj,
                    errors: v.errors,
                    status: v.status
                };
            }));
        }
    }, [defaultAccount, accountsList, validateRow]);

    // Fetch History
    const fetchHistory = useCallback(async () => {
        setLoadingHistory(true);
        try {
            const res = await axios.get(`${API_BASE}/shipping/bulk-history`);
            if (res.data?.success) {
                setHistoryList(res.data.shipments || []);
            }
        } catch (err) {
            console.error('Failed to load bulk history:', err);
        } finally {
            setLoadingHistory(false);
        }
    }, [API_BASE]);

    useEffect(() => {
        if (activeTab === 'history') {
            fetchHistory();
        }
    }, [activeTab, fetchHistory]);

    // ── Generate and Download Excel Template ──────────────────────────────
    const handleDownloadTemplate = () => {
        const sampleAccount = (defaultAccount && defaultAccount.toLowerCase() !== 'all')
            ? defaultAccount
            : (accountsList.find(a => a && a.toLowerCase() !== 'all') || '89R907');

        const sampleRows = [
            {
                ShipNumber: 'SHIP001',
                AccountNO: sampleAccount,
                Shippercontactname: 'Export Manager',
                shippercompany: companyName || 'Prime Logistics PK',
                Shippercountry: 'PK',
                ShipperAddressone: 'Plot 42 Commercial Area Phase 2',
                ShipperAddresstwo: 'Industrial Area',
                ShipperCity: userCity || 'Karachi',
                ShipperPostalCode: '74900',
                ShipperState: 'Sindh',
                ShipperTELE: '+923001234567',
                ShipperEmail: currentUser || 'shipper@example.com',
                ShipperTaxID: 'PK1234567',
                ContactName: 'John Doe',
                Companyname: 'Global Tech Corp',
                Country: 'US',
                Addressone: '350 5th Ave',
                Addresstwo: 'Suite 2100',
                Addressthree: '',
                City: 'New York',
                StateProvOther: 'NY',
                PostalCode: '10118',
                Telephone: '+12125551234',
                Ext: '',
                ResidentialInd: 'N',
                ConsigneeEmail: 'johndoe@globaltech.com',
                PackagingType: '02',
                CustomsValue: 120.00,
                Weight: 2.5,
                Length: 25,
                Width: 20,
                Height: 15,
                UnitofMeasure: 'KGS',
                DescriptionofGoods: 'Leather Samples & Handcrafted Goods',
                OriginCountry: 'PK',
                TariffCode: '4202.21',
                PartNumber: 'PRD-LEATH-01',
                NumberOfUnits: 2,
                UnitPrice: 60.00,
                UnitPriceCurrency: 'USD',
                InVoiceTaxPaid: 'N',
                UPC: '',
                ASIN: '',
                DocumentsofNoCommercialValue: 'N',
                GNIFC: 'N',
                PkgDeclValue: 120.00,
                Service: '65',
                DeliveryConfirm: 'Signature',
                ShipperRelease: 'N',
                RetofDocuments: 'N',
                SaturdayDeliver: 'N',
                CarbonNeutral: 'N',
                LargePackage: 'N',
                Addlhandling: 'N',
                Referenceone: 'REF-2026-001',
                Referencetwo: 'PO-88992',
                Referencethree: ''
            },
            {
                ShipNumber: 'SHIP002',
                AccountNO: sampleAccount,
                Shippercontactname: 'Export Operations',
                shippercompany: companyName || 'Textile Exports Ltd',
                Shippercountry: 'PK',
                ShipperAddressone: 'Sector 15 SITE Area',
                ShipperAddresstwo: '',
                ShipperCity: userCity || 'Karachi',
                ShipperPostalCode: '75700',
                ShipperState: 'Sindh',
                ShipperTELE: '+923219876543',
                ShipperEmail: currentUser || 'ops@textileexports.pk',
                ShipperTaxID: 'PK9988776',
                ContactName: 'Sarah Jenkins',
                Companyname: 'Apex Retail UK',
                Country: 'GB',
                Addressone: '10 Downing Way',
                Addresstwo: 'Central Park',
                Addressthree: '',
                City: 'London',
                StateProvOther: '',
                PostalCode: 'EC1A 1BB',
                Telephone: '+442079460912',
                Ext: '',
                ResidentialInd: 'N',
                ConsigneeEmail: 's.jenkins@apexretail.co.uk',
                PackagingType: '02',
                CustomsValue: 85.00,
                Weight: 1.8,
                Length: 30,
                Width: 20,
                Height: 10,
                UnitofMeasure: 'KGS',
                DescriptionofGoods: 'Cotton Apparel & Samples',
                OriginCountry: 'PK',
                TariffCode: '6109.10',
                PartNumber: 'TX-COTTON-09',
                NumberOfUnits: 5,
                UnitPrice: 17.00,
                UnitPriceCurrency: 'USD',
                InVoiceTaxPaid: 'N',
                UPC: '',
                ASIN: '',
                DocumentsofNoCommercialValue: 'N',
                GNIFC: 'N',
                PkgDeclValue: 85.00,
                Service: '65',
                DeliveryConfirm: '',
                ShipperRelease: 'N',
                RetofDocuments: 'N',
                SaturdayDeliver: 'N',
                CarbonNeutral: 'Y',
                LargePackage: 'N',
                Addlhandling: 'N',
                Referenceone: 'INV-UK-7721',
                Referencetwo: '',
                Referencethree: ''
            }
        ];

        const guideRows = [
            { Field: 'ShipNumber', Required: 'Optional', Description: 'Shipment group ID. Rows with same ShipNumber combine into 1 multi-package shipment' },
            { Field: 'AccountNO', Required: 'Required', Description: 'UPS Shipper Account number to charge. Must be authorized for user' },
            { Field: 'Shippercontactname', Required: 'Required', Description: 'Shipper contact person name' },
            { Field: 'shippercompany', Required: 'Required', Description: 'Shipper company name' },
            { Field: 'Shippercountry', Required: 'Required', Description: '2-letter origin country ISO code (e.g. PK, US, GB)' },
            { Field: 'ShipperAddressone', Required: 'Required', Description: 'Shipper primary street address (max 35 chars)' },
            { Field: 'ShipperCity', Required: 'Required', Description: 'Shipper city name' },
            { Field: 'ShipperPostalCode', Required: 'Optional/Req', Description: 'Shipper postal code' },
            { Field: 'ShipperTELE', Required: 'Required', Description: 'Shipper telephone number' },
            { Field: 'ContactName', Required: 'Required', Description: 'Consignee / Recipient contact name' },
            { Field: 'Companyname', Required: 'Optional', Description: 'Consignee company name (defaults to ContactName)' },
            { Field: 'Country', Required: 'Required', Description: '2-letter destination country ISO code (e.g. US, GB, AE, CA)' },
            { Field: 'Addressone', Required: 'Required', Description: 'Recipient street address (max 35 chars)' },
            { Field: 'City', Required: 'Required', Description: 'Recipient destination city' },
            { Field: 'StateProvOther', Required: 'Required for US/CA', Description: '2-letter state code for US/CA/IE (e.g. NY, CA, ON)' },
            { Field: 'PostalCode', Required: 'Required', Description: 'Recipient ZIP or Postal Code' },
            { Field: 'Telephone', Required: 'Required', Description: 'Recipient contact phone number' },
            { Field: 'PackagingType', Required: 'Required', Description: '02 = Customer Box/Package, 01 = UPS Letter, 24 = 25KG Box, 25 = 10KG Box' },
            { Field: 'Weight', Required: 'Required', Description: 'Package weight in KGS or LBS (e.g. 2.5)' },
            { Field: 'Length, Width, Height', Required: 'Required', Description: 'Package dimensions in CM or IN' },
            { Field: 'DescriptionofGoods', Required: 'Required', Description: 'Product / merchandise description' },
            { Field: 'TariffCode', Required: 'Required', Description: 'HS Tariff Code for customs (e.g. 4202.21)' },
            { Field: 'UnitPrice', Required: 'Required', Description: 'Unit price for customs invoice' },
            { Field: 'NumberOfUnits', Required: 'Required', Description: 'Quantity of units (e.g. 1, 2, 10)' },
            { Field: 'OriginCountry', Required: 'Required', Description: '2-letter country where product was made (e.g. PK)' },
            { Field: 'Service', Required: 'Default: 65', Description: 'UPS Service Code (65 = UPS Saver, 07 = UPS Express, 11 = UPS Standard)' },
            { Field: 'DocumentsofNoCommercialValue', Required: 'Optional', Description: 'Y if document shipment without commercial invoice, otherwise N' }
        ];

        const wb = XLSX.utils.book_new();
        const wsShipments = XLSX.utils.json_to_sheet(sampleRows, { header: BULK_COLUMNS });
        const wsGuide = XLSX.utils.json_to_sheet(guideRows);

        XLSX.utils.book_append_sheet(wb, wsShipments, 'BulkShipments');
        XLSX.utils.book_append_sheet(wb, wsGuide, 'FieldGuide');

        XLSX.writeFile(wb, `Bulk_Shipment_Template_${new Date().toISOString().split('T')[0]}.xlsx`);
    };

    // ── Excel Upload and Parsing ──────────────────────────────────────────
    const handleFileUpload = (file) => {
        if (!file) return;
        setFileName(file.name);
        const reader = new FileReader();

        reader.onload = (e) => {
            try {
                const data = new Uint8Array(e.target.result);
                const workbook = XLSX.read(data, { type: 'array' });
                const firstSheetName = workbook.SheetNames[0];
                const worksheet = workbook.Sheets[firstSheetName];
                const rawJson = XLSX.utils.sheet_to_json(worksheet, { defval: '' });

                if (!rawJson || rawJson.length === 0) {
                    alert('The uploaded Excel sheet contains no data rows.');
                    return;
                }

                // Clean & normalize rows
                const validated = rawJson.map((row, idx) => {
                    const normalized = {};
                    // Case-insensitive key match
                    Object.keys(row).forEach(rawKey => {
                        const cleanKey = rawKey.trim();
                        const matchedCol = BULK_COLUMNS.find(c => c.toLowerCase() === cleanKey.toLowerCase());
                        if (matchedCol) {
                            normalized[matchedCol] = row[rawKey];
                        } else {
                            normalized[cleanKey] = row[rawKey];
                        }
                    });

                    const v = validateRow(normalized, accountsList, defaultAccount);

                    return {
                        id: `row-${idx + 1}`,
                        rowIndex: idx + 1,
                        shipNumber: normalized.ShipNumber || `SHIP-${String(idx + 1).padStart(3, '0')}`,
                        data: v.rowObj,
                        errors: v.errors,
                        status: v.status,
                        result: null
                    };
                });

                setParsedRows(validated);
            } catch (err) {
                console.error('Failed to parse Excel file:', err);
                alert('Failed to parse Excel file: ' + err.message);
            }
        };

        reader.readAsArrayBuffer(file);
    };

    const handleDrop = (e) => {
        e.preventDefault();
        setIsDragging(false);
        if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
            handleFileUpload(e.dataTransfer.files[0]);
        }
    };

    // ── Generate Bulk Shipments Process ────────────────────────────────────
    const handleStartBatch = async () => {
        const rowsToProcess = parsedRows.filter(r => r.status === 'ready' || r.status === 'failed');
        if (rowsToProcess.length === 0) {
            alert('No valid rows ready to process. Please resolve validation errors or upload rows.');
            return;
        }

        setIsProcessing(true);
        setCancelRequested(false);
        setTotalToProcess(rowsToProcess.length);
        setProcessedCount(0);
        setProcessProgress(0);
        setLiveLogs([]);

        const updatedRows = [...parsedRows];

        for (let i = 0; i < rowsToProcess.length; i++) {
            if (cancelRequested) {
                setLiveLogs(prev => [`[Batch] Processing stopped by user.`, ...prev]);
                break;
            }

            const item = rowsToProcess[i];
            const rowIndexInMaster = updatedRows.findIndex(r => r.id === item.id);
            if (rowIndexInMaster === -1) continue;

            // Update status to processing
            updatedRows[rowIndexInMaster] = {
                ...updatedRows[rowIndexInMaster],
                status: 'processing'
            };
            setParsedRows([...updatedRows]);

            const row = item.data;
            let effectiveAccount = row.effectiveAccount || defaultAccount;
            if (!effectiveAccount || effectiveAccount.toLowerCase() === 'all') {
                effectiveAccount = defaultAccount;
            }
            if (!effectiveAccount || effectiveAccount.toLowerCase() === 'all') {
                effectiveAccount = accountsList.find(a => a && a.toLowerCase() !== 'all') || '89R907';
            }

            // Build shipment payload matching upsService.createShipment format
            const isDoc = ['Y', 'YES', 'TRUE', '1'].includes(String(row.DocumentsofNoCommercialValue || '').trim().toUpperCase());
            const packagingCode = String(row.PackagingType || '02').trim();
            const weightVal = parseFloat(row.Weight) || 0.5;

            const shipmentPayload = {
                accountNumber: effectiveAccount,
                account: effectiveAccount,
                shipmentNumber: row.ShipNumber || item.shipNumber,
                description: row.DescriptionofGoods || row.productdisctription || 'General Merchandise',
                service: row.Service || '65',
                productType: isDoc ? 'DOCUMENTS' : 'NON-DOCUMENTS',
                isDocumentNoCommercial: isDoc,
                isDDP: false,
                reference1: (row.Referenceone && String(row.Referenceone).trim()) ? String(row.Referenceone).trim() : '',
                reference2: (row.Referencetwo && String(row.Referencetwo).trim()) ? String(row.Referencetwo).trim() : '',
                reference3: (row.Referencethree && String(row.Referencethree).trim()) ? String(row.Referencethree).trim() : '',
                saturdayDelivery: ['Y', 'YES', 'TRUE', '1'].includes(String(row.SaturdayDeliver || '').trim().toUpperCase()),
                carbonNeutral: ['Y', 'YES', 'TRUE', '1'].includes(String(row.CarbonNeutral || '').trim().toUpperCase()),
                deliveryConfirmation: row.DeliveryConfirm || (['Y', 'YES', 'TRUE', '1'].includes(String(row.signcheck || '').trim().toUpperCase()) ? 'Signature' : null),

                shipper: {
                    name: row.Shippercontactname || row.shippercompany || 'Shipper',
                    attention: row.Shippercontactname || 'Shipper',
                    company: row.shippercompany || row.Shippercontactname || 'Shipper',
                    country: (row.Shippercountry || 'PK').trim().toUpperCase(),
                    address1: row.ShipperAddressone || 'Address Line 1',
                    address2: row.ShipperAddresstwo || '',
                    city: row.ShipperCity || userCity || 'Karachi',
                    postalCode: row.ShipperPostalCode || '74900',
                    state: row.ShipperState || '',
                    phone: row.ShipperTELE || '+923001234567',
                    email: row.ShipperEmail || currentUser || '',
                    taxId: row.ShipperTaxID || ''
                },

                consignee: {
                    name: row.ContactName || row.Companyname || 'Consignee',
                    attention: row.ContactName || 'Consignee',
                    company: row.Companyname || row.ContactName || 'Consignee',
                    country: (row.Country || '').trim().toUpperCase(),
                    address1: row.Addressone || '',
                    address2: row.Addresstwo || '',
                    address3: row.Addressthree || '',
                    city: row.City || '',
                    state: (row.StateProvOther || '').trim().toUpperCase(),
                    postalCode: row.PostalCode || '',
                    phone: row.Telephone || '+10000000000',
                    phoneExtension: row.Ext || '',
                    email: row.ConsigneeEmail || '',
                    type: ['Y', 'YES', 'TRUE', '1'].includes(String(row.ResidentialInd || '').trim().toUpperCase()) ? '01' : '02'
                },

                packages: [
                    {
                        packageType: packagingCode,
                        weight: weightVal,
                        actualWeight: weightVal,
                        weightUnit: String(row.UnitofMeasure || 'KGS').toUpperCase().includes('LB') ? 'LBS' : 'KGS',
                        length: parseFloat(row.Length) || 10,
                        width: parseFloat(row.Width) || 10,
                        height: parseFloat(row.Height) || 10,
                        dimUnit: String(row.UnitofMeasure || 'CM').toUpperCase().includes('IN') ? 'IN' : 'CM',
                        description: row.DescriptionofGoods || row.productdisctription || 'Package 1',
                        additionalHandling: ['Y', 'YES', 'TRUE', '1'].includes(String(row.Addlhandling || '').trim().toUpperCase()),
                        largePackage: ['Y', 'YES', 'TRUE', '1'].includes(String(row.LargePackage || '').trim().toUpperCase()),
                        declaredValue: parseFloat(row.PkgDeclValue || row.CustomsValue) || 0
                    }
                ],

                products: isDoc ? [] : [
                    {
                        description: row.DescriptionofGoods || row.productdisctription || 'Merchandise',
                        hsCode: row.TariffCode || row.producthscode || '',
                        value: parseFloat(row.UnitPrice || row.productunitprice || row.CustomsValue) || 10,
                        quantity: parseInt(row.NumberOfUnits || row.productquantity) || 1,
                        unit: 'EA',
                        countryOfOrigin: (row.OriginCountry || row.productorigincountry || row.Shippercountry || 'PK').trim().toUpperCase(),
                        partNumber: row.PartNumber || row.UPC || row.ASIN || ''
                    }
                ],

                exportReason: 'GIFT',
                currencyType: row.UnitPriceCurrency || 'USD',
                declarationStatement: 'I hereby certify that the information on this invoice is true and correct.',
                includeCommercialInvoice: !isDoc,
                shipmentInsuranceDeclaredValue: parseFloat(row.PkgDeclValue || row.CustomsValue) || 0
            };

            try {
                setLiveLogs(prev => [`[Row ${item.rowIndex}] Creating shipment for ${shipmentPayload.consignee.name} (${shipmentPayload.consignee.country})...`, ...prev]);

                const response = await axios.post(`${API_BASE}/shipping/bulk-create`, shipmentPayload);

                if (response.data?.success) {
                    const result = response.data;
                    updatedRows[rowIndexInMaster] = {
                        ...updatedRows[rowIndexInMaster],
                        status: 'success',
                        result: result,
                        trackingNumber: result.trackingNumber,
                        labels: result.labels || (result.labelImage ? [result.labelImage] : []),
                        upsCommercialInvoice: result.upsCommercialInvoice || result.commercialInvoice || null,
                        invoiceNo: result.invoiceNo || null,
                        commodityInvoiceNo: result.commodityInvoiceNo || null
                    };

                    setLiveLogs(prev => [
                        `✅ [Row ${item.rowIndex}] SUCCESS! Tracking: ${result.trackingNumber}`,
                        ...prev
                    ]);
                } else {
                    throw new Error(response.data?.message || 'Shipment creation failed');
                }
            } catch (err) {
                const errMsg = err.response?.data?.message || err.message || 'UPS API Error';
                updatedRows[rowIndexInMaster] = {
                    ...updatedRows[rowIndexInMaster],
                    status: 'failed',
                    errorMessage: errMsg
                };
                setLiveLogs(prev => [
                    `❌ [Row ${item.rowIndex}] FAILED: ${errMsg}`,
                    ...prev
                ]);
            }

            setParsedRows([...updatedRows]);
            setProcessedCount(i + 1);
            setProcessProgress(Math.round(((i + 1) / rowsToProcess.length) * 100));
        }

        setIsProcessing(false);
    };

    // ── Bulk Print All UPS Labels ──────────────────────────────────────────
    const handlePrintAllLabels = () => {
        const successful = parsedRows.filter(r => r.status === 'success' && r.labels && r.labels.length > 0);
        if (successful.length === 0) {
            alert('No successfully generated shipping labels available to print.');
            return;
        }

        const allLabels = [];
        successful.forEach(r => {
            r.labels.forEach(lbl => allLabels.push(lbl));
        });

        const win = window.open('', '_blank');
        if (!win) {
            alert('Popup blocked. Please allow popups to print labels.');
            return;
        }

        const html = `<!DOCTYPE html>
<html>
<head>
  <title>Bulk UPS Shipping Labels (${allLabels.length})</title>
  <style>
    @page { size: A4; margin: 10mm; }
    body { margin: 0; padding: 0; font-family: sans-serif; background: #fff; }
    .label-page {
      page-break-after: always;
      display: flex;
      flex-direction: column;
      justify-content: center;
      align-items: center;
      min-height: 90vh;
      box-sizing: border-box;
      padding: 10px;
    }
    .label-page img {
      max-width: 100%;
      height: auto;
      border: 1px solid #ddd;
    }
    @media print {
      body { background: transparent; }
      .label-page img { border: none; }
    }
  </style>
</head>
<body>
  ${allLabels.map((lbl, idx) => {
            const isPdf = lbl.startsWith('JVBERi');
            if (isPdf) {
                return `<div class="label-page"><h3>Commercial Invoice PDF (Shipment #${idx + 1})</h3><p>Electronic document</p></div>`;
            }
            return `<div class="label-page"><img src="data:image/gif;base64,${lbl}" alt="UPS Label" /></div>`;
        }).join('')}
  <script>
    window.onload = function() {
      setTimeout(function() { window.print(); }, 400);
    };
  </script>
</body>
</html>`;

        win.document.write(html);
        win.document.close();
    };

    // ── Bulk Print All Commercial Invoices ─────────────────────────────────
    const handlePrintAllCommercialInvoices = () => {
        const successful = parsedRows.filter(r => r.status === 'success');
        if (successful.length === 0) {
            alert('No successfully generated shipments available for commercial invoices.');
            return;
        }

        const invoicePagesHTML = successful.map(r => {
            const row = r.data;
            const res = r.result || {};
            const ciData = {
                shipper: {
                    name: row.Shippercontactname || row.shippercompany || '',
                    address1: row.ShipperAddressone || '',
                    city: row.ShipperCity || userCity || '',
                    state: row.ShipperState || '',
                    country: row.Shippercountry || 'PK'
                },
                destination: {
                    name: row.ContactName || row.Companyname || '',
                    company: row.Companyname || '',
                    address1: row.Addressone || '',
                    address2: row.Addresstwo || '',
                    address3: row.Addressthree || '',
                    city: row.City || '',
                    state: row.StateProvOther || '',
                    postalCode: row.PostalCode || '',
                    country: row.Country || '',
                    phone: row.Telephone || '',
                    email: row.ConsigneeEmail || ''
                },
                products: [
                    {
                        description: row.DescriptionofGoods || row.productdisctription || 'Merchandise',
                        hsCode: row.TariffCode || row.producthscode || 'N/A',
                        quantity: parseInt(row.NumberOfUnits || row.productquantity) || 1,
                        uom: 'PCS',
                        value: parseFloat(row.UnitPrice || row.productunitprice || row.CustomsValue) || 10,
                        countryOfOrigin: row.OriginCountry || row.productorigincountry || row.Shippercountry || 'PK'
                    }
                ],
                invoiceNo: res.invoiceNo || r.invoiceNo || '',
                commodityInvoiceNo: res.commodityInvoiceNo || r.commodityInvoiceNo || '',
                exportReason: 'GIFT',
                countryOfOrigin: row.OriginCountry || row.productorigincountry || row.Shippercountry || 'PK',
                declarationStatement: 'I hereby certify that the information on this invoice is true and correct.',
                currencyType: row.UnitPriceCurrency || 'USD',
                trackingNumber: r.trackingNumber || 'N/A',
                date: new Date().toLocaleDateString('en-GB')
            };

            return generateCommercialInvoiceHTML(ciData);
        }).join('');

        const win = window.open('', '_blank');
        if (!win) {
            alert('Popup blocked. Please allow popups to view and print invoices.');
            return;
        }

        const html = `<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8">
  <title>Bulk Commercial Invoices (${successful.length})</title>
  <style>
    @page { size: A4 portrait; margin: 0; }
    body { margin: 0; padding: 0; background: #525659; }
    .page { page-break-after: always; }
  </style>
</head>
<body>
  ${invoicePagesHTML}
  <script>
    window.onload = function() {
      setTimeout(function() { window.print(); }, 500);
    };
  </script>
</body>
</html>`;

        win.document.write(html);
        win.document.close();
    };

    // ── Export Batch Results to Excel ──────────────────────────────────────
    const handleExportBatchResults = () => {
        if (parsedRows.length === 0) {
            alert('No rows available to export.');
            return;
        }

        const exportData = parsedRows.map(r => ({
            'Row #': r.rowIndex,
            'Ship Number': r.shipNumber,
            'Status': r.status.toUpperCase(),
            'Tracking #': r.trackingNumber || '',
            'Account #': r.data.effectiveAccount || '',
            'Consignee Name': r.data.ContactName || r.data.Companyname || '',
            'Destination Country': r.data.Country || '',
            'Destination City': r.data.City || '',
            'Weight (kg)': r.data.Weight || '',
            'Description': r.data.DescriptionofGoods || r.data.productdisctription || '',
            'Invoice #': r.invoiceNo || '',
            'Commodity Invoice #': r.commodityInvoiceNo || '',
            'Error Details': r.errorMessage || (r.errors.length > 0 ? r.errors.join('; ') : '')
        }));

        const ws = XLSX.utils.json_to_sheet(exportData);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, 'BatchResults');
        XLSX.writeFile(wb, `Bulk_Shipment_Results_${new Date().toISOString().split('T')[0]}.xlsx`);
    };

    // ── Individual View Actions ───────────────────────────────────────────
    const handleViewSingleLabel = (row) => {
        if (row.labels && row.labels.length > 0) {
            setLabelModalData(row.labels);
        } else {
            alert('No label image found for this shipment.');
        }
    };

    const handleViewSingleInvoice = (row) => {
        // If UPS Electronic Invoice exists and is PDF, show that or HTML
        const rowData = row.data;
        const res = row.result || {};
        const ciData = {
            shipper: {
                name: rowData.Shippercontactname || rowData.shippercompany || '',
                address1: rowData.ShipperAddressone || '',
                city: rowData.ShipperCity || userCity || '',
                state: rowData.ShipperState || '',
                country: rowData.Shippercountry || 'PK'
            },
            destination: {
                name: rowData.ContactName || rowData.Companyname || '',
                company: rowData.Companyname || '',
                address1: rowData.Addressone || '',
                address2: rowData.Addresstwo || '',
                address3: rowData.Addressthree || '',
                city: rowData.City || '',
                state: rowData.StateProvOther || '',
                postalCode: rowData.PostalCode || '',
                country: rowData.Country || '',
                phone: rowData.Telephone || '',
                email: rowData.ConsigneeEmail || ''
            },
            products: [
                {
                    description: rowData.DescriptionofGoods || rowData.productdisctription || 'Merchandise',
                    hsCode: rowData.TariffCode || rowData.producthscode || 'N/A',
                    quantity: parseInt(rowData.NumberOfUnits || rowData.productquantity) || 1,
                    uom: 'PCS',
                    value: parseFloat(rowData.UnitPrice || rowData.productunitprice || rowData.CustomsValue) || 10,
                    countryOfOrigin: rowData.OriginCountry || rowData.productorigincountry || rowData.Shippercountry || 'PK'
                }
            ],
            invoiceNo: res.invoiceNo || row.invoiceNo || '',
            commodityInvoiceNo: res.commodityInvoiceNo || row.commodityInvoiceNo || '',
            exportReason: 'Sale',
            countryOfOrigin: rowData.OriginCountry || rowData.productorigincountry || rowData.Shippercountry || 'PK',
            declarationStatement: 'I hereby certify that the information on this invoice is true and correct.',
            currencyType: rowData.UnitPriceCurrency || 'USD',
            trackingNumber: row.trackingNumber || 'N/A',
            date: new Date().toLocaleDateString('en-GB')
        };

        const html = generateCommercialInvoiceHTML(ciData);
        setCiModalData({ html, tracking: row.trackingNumber, rawPdf: row.upsCommercialInvoice });
    };

    // Filtered rows for table view
    const displayedRows = parsedRows.filter(r => {
        if (filterStatus === 'ready') return r.status === 'ready';
        if (filterStatus === 'error') return r.status === 'error' || r.status === 'failed';
        if (filterStatus === 'success') return r.status === 'success';
        return true;
    });

    const readyCount = parsedRows.filter(r => r.status === 'ready').length;
    const errorCount = parsedRows.filter(r => r.status === 'error' || r.status === 'failed').length;
    const successCount = parsedRows.filter(r => r.status === 'success').length;
    const totalWeight = parsedRows.reduce((sum, r) => sum + (parseFloat(r.data.Weight) || 0), 0);

    return (
        <div className="bulk-container">
            {/* ── Top Header ── */}
            <header className="bulk-header">
                <div className="bulk-header-left">
                    <button className="bulk-back-btn" onClick={onBack}>
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M19 12H5M12 19l-7-7 7-7" /></svg>
                        Back
                    </button>
                    <div className="bulk-header-title">
                        <div className="bulk-header-icon">📑</div>
                        <div>
                            <h1>Bulk Shipment Module</h1>
                            <p>Generate bulk UPS labels and commercial invoices from Excel sheets {companyName ? `• ${companyName}` : ''}</p>
                        </div>
                    </div>
                </div>

                <div className="bulk-header-tabs">
                    <button
                        className={`bulk-tab-btn ${activeTab === 'upload' ? 'active' : ''}`}
                        onClick={() => setActiveTab('upload')}
                    >
                        📤 Upload & Generate
                    </button>
                    <button
                        className={`bulk-tab-btn ${activeTab === 'history' ? 'active' : ''}`}
                        onClick={() => setActiveTab('history')}
                    >
                        📜 Past Batches
                    </button>
                    <button
                        className={`bulk-tab-btn ${activeTab === 'guide' ? 'active' : ''}`}
                        onClick={() => setActiveTab('guide')}
                    >
                        📖 Format Guide
                    </button>
                </div>
            </header>

            <div className="bulk-content">
                {activeTab === 'upload' && (
                    <>
                        {/* ── Download Template Card ── */}
                        <div className="bulk-template-card">
                            <div className="bulk-template-info">
                                <h2>
                                    Official Bulk Shipment Excel Format
                                    <span className="bulk-template-badge">57 Standard Fields</span>
                                </h2>
                                <p>
                                    Download the pre-formatted Excel template (.xlsx). Fill in your shipment details including accounts, shipper, consignee addresses, package weights, and product descriptions, then upload to generate UPS Labels & Commercial Invoices.
                                </p>
                            </div>
                            <button className="bulk-download-btn" onClick={handleDownloadTemplate}>
                                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4M7 10l5 5 5-5M12 15V3" /></svg>
                                Download Excel Template (.xlsx)
                            </button>
                        </div>

                        {/* ── Account Selector / Fallback & File Upload ── */}
                        <div className="bulk-config-card">
                            <div className="bulk-config-item">
                                <span className="bulk-config-label">Default UPS Account:</span>
                                <select
                                    className="bulk-select"
                                    value={defaultAccount}
                                    onChange={(e) => {
                                        const newDef = e.target.value;
                                        setDefaultAccount(newDef);
                                        if (parsedRows.length > 0) {
                                            setParsedRows(prev => prev.map(r => {
                                                if (r.status === 'success' || r.status === 'processing') return r;
                                                const updatedData = { ...r.data };
                                                if (!updatedData.AccountNO || updatedData.AccountNO.toLowerCase() === 'all') {
                                                    updatedData.effectiveAccount = newDef;
                                                }
                                                const v = validateRow(updatedData, accountsList, newDef);
                                                return {
                                                    ...r,
                                                    data: v.rowObj,
                                                    errors: v.errors,
                                                    status: v.status
                                                };
                                            }));
                                        }
                                    }}
                                >
                                    {accountsList.length === 0 ? (
                                        <option value="">No accounts assigned</option>
                                    ) : (
                                        accountsList.map(acc => (
                                            <option key={acc} value={acc}>
                                                {acc} {companySlug ? '(Company Account)' : '(Assigned Account)'}
                                            </option>
                                        ))
                                    )}
                                </select>
                                {hasAllAccess && (
                                    <span style={{ fontSize: '11px', background: '#dcfce7', color: '#15803d', border: '1px solid #bbf7d0', padding: '2px 8px', borderRadius: '10px', fontWeight: 600 }}>
                                        Full Access
                                    </span>
                                )}
                                <span style={{ fontSize: '12px', color: '#64748b' }}>
                                    (Used if row AccountNO is left blank or set to default)
                                </span>
                            </div>

                            {parsedRows.length > 0 && (
                                <button
                                    className="bulk-btn bulk-btn-secondary"
                                    onClick={() => {
                                        setParsedRows([]);
                                        setFileName('');
                                        setLiveLogs([]);
                                    }}
                                >
                                    Clear Uploaded Data
                                </button>
                            )}
                        </div>

                        {/* ── Dropzone ── */}
                        {parsedRows.length === 0 && (
                            <div
                                className={`bulk-upload-dropzone ${isDragging ? 'drag-active' : ''}`}
                                onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
                                onDragLeave={() => setIsDragging(false)}
                                onDrop={handleDrop}
                                onClick={() => fileInputRef.current?.click()}
                            >
                                <input
                                    type="file"
                                    ref={fileInputRef}
                                    style={{ display: 'none' }}
                                    accept=".xlsx, .xls, .csv"
                                    onChange={(e) => {
                                        if (e.target.files && e.target.files[0]) {
                                            handleFileUpload(e.target.files[0]);
                                        }
                                    }}
                                />
                                <div className="bulk-dropzone-icon">📁</div>
                                <h3>Upload Completed Excel File</h3>
                                <p>Drag and drop your filled Excel file here, or click to browse (.xlsx, .xls, .csv)</p>
                                <span className="bulk-browse-btn">Choose File</span>
                            </div>
                        )}

                        {/* ── Stats Overview ── */}
                        {parsedRows.length > 0 && (
                            <div className="bulk-stats-grid">
                                <div className="bulk-stat-card" style={{ '--stat-color': '#0284c7' }}>
                                    <div className="bulk-stat-title">Total Shipments</div>
                                    <div className="bulk-stat-value">{parsedRows.length}</div>
                                    <div className="bulk-stat-sub">From {fileName || 'Uploaded Sheet'}</div>
                                </div>
                                <div className="bulk-stat-card" style={{ '--stat-color': '#10b981' }}>
                                    <div className="bulk-stat-title">Ready to Process</div>
                                    <div className="bulk-stat-value" style={{ color: '#059669' }}>{readyCount}</div>
                                    <div className="bulk-stat-sub">Validated without errors</div>
                                </div>
                                <div className="bulk-stat-card" style={{ '--stat-color': '#ef4444' }}>
                                    <div className="bulk-stat-title">Validation Errors</div>
                                    <div className="bulk-stat-value" style={{ color: errorCount > 0 ? '#dc2626' : '#64748b' }}>{errorCount}</div>
                                    <div className="bulk-stat-sub">{errorCount > 0 ? 'Requires attention' : 'All rows valid'}</div>
                                </div>
                                <div className="bulk-stat-card" style={{ '--stat-color': '#8b5cf6' }}>
                                    <div className="bulk-stat-title">Total Weight</div>
                                    <div className="bulk-stat-value">{totalWeight.toFixed(2)} <span style={{ fontSize: '15px' }}>KGS</span></div>
                                    <div className="bulk-stat-sub">Across all packages</div>
                                </div>
                            </div>
                        )}

                        {/* ── Live Progress Section ── */}
                        {isProcessing && (
                            <div className="bulk-progress-card animated fadeIn">
                                <div className="bulk-progress-header">
                                    <div className="bulk-progress-title">
                                        <div className="bulk-spinner bulk-spinner-dark"></div>
                                        Generating Shipments via UPS API...
                                    </div>
                                    <div className="bulk-progress-counter">
                                        {processedCount} / {totalToProcess} ({processProgress}%)
                                    </div>
                                </div>

                                <div className="bulk-progress-bar-wrapper">
                                    <div className="bulk-progress-bar-fill" style={{ width: `${processProgress}%` }}></div>
                                </div>

                                <div className="bulk-progress-status-text">
                                    <span>Please keep this window open while labels are generated.</span>
                                    <button
                                        style={{ background: 'none', border: 'none', color: '#ef4444', fontWeight: 600, cursor: 'pointer' }}
                                        onClick={() => setCancelRequested(true)}
                                    >
                                        Cancel Remaining
                                    </button>
                                </div>

                                {liveLogs.length > 0 && (
                                    <div className="bulk-live-log">
                                        {liveLogs.map((log, idx) => (
                                            <div
                                                key={idx}
                                                className={`bulk-live-log-entry ${log.includes('SUCCESS') ? 'success' : log.includes('FAILED') ? 'error' : ''}`}
                                            >
                                                {log}
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        )}

                        {/* ── Action Toolbar ── */}
                        {parsedRows.length > 0 && (
                            <div className="bulk-actions-toolbar">
                                <div className="bulk-toolbar-left">
                                    <button
                                        className="bulk-btn bulk-btn-primary"
                                        onClick={handleStartBatch}
                                        disabled={isProcessing || readyCount === 0}
                                    >
                                        {isProcessing ? (
                                            <>
                                                <div className="bulk-spinner"></div>
                                                Processing Batch...
                                            </>
                                        ) : (
                                            <>
                                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polygon points="5 3 19 12 5 21 5 3"></polygon></svg>
                                                Generate {readyCount} Shipments (Labels & Invoices)
                                            </>
                                        )}
                                    </button>

                                    <div style={{ display: 'flex', gap: '4px' }}>
                                        <button
                                            className={`bulk-btn ${filterStatus === 'all' ? 'bulk-btn-secondary' : ''}`}
                                            style={{ background: filterStatus === 'all' ? '#e2e8f0' : 'transparent', border: 'none' }}
                                            onClick={() => setFilterStatus('all')}
                                        >
                                            All ({parsedRows.length})
                                        </button>
                                        <button
                                            className={`bulk-btn ${filterStatus === 'ready' ? 'bulk-btn-secondary' : ''}`}
                                            style={{ background: filterStatus === 'ready' ? '#e2e8f0' : 'transparent', border: 'none', color: '#059669' }}
                                            onClick={() => setFilterStatus('ready')}
                                        >
                                            Ready ({readyCount})
                                        </button>
                                        <button
                                            className={`bulk-btn ${filterStatus === 'error' ? 'bulk-btn-secondary' : ''}`}
                                            style={{ background: filterStatus === 'error' ? '#e2e8f0' : 'transparent', border: 'none', color: '#dc2626' }}
                                            onClick={() => setFilterStatus('error')}
                                        >
                                            Errors ({errorCount})
                                        </button>
                                        <button
                                            className={`bulk-btn ${filterStatus === 'success' ? 'bulk-btn-secondary' : ''}`}
                                            style={{ background: filterStatus === 'success' ? '#e2e8f0' : 'transparent', border: 'none', color: '#2563eb' }}
                                            onClick={() => setFilterStatus('success')}
                                        >
                                            Generated ({successCount})
                                        </button>
                                    </div>
                                </div>

                                <div className="bulk-toolbar-right">
                                    <button
                                        className="bulk-btn bulk-btn-purple"
                                        onClick={handlePrintAllLabels}
                                        disabled={successCount === 0}
                                        title="Print all UPS shipping labels in one batch"
                                    >
                                        🖨️ Print All Labels ({successCount})
                                    </button>

                                    <button
                                        className="bulk-btn bulk-btn-success"
                                        onClick={handlePrintAllCommercialInvoices}
                                        disabled={successCount === 0}
                                        title="Print all commercial invoices in one batch"
                                    >
                                        📄 Print All Invoices ({successCount})
                                    </button>

                                    <button
                                        className="bulk-btn bulk-btn-secondary"
                                        onClick={handleExportBatchResults}
                                    >
                                        📊 Export Results (Excel)
                                    </button>
                                </div>
                            </div>
                        )}

                        {/* ── Data Grid ── */}
                        {parsedRows.length > 0 && (
                            <div className="bulk-table-container">
                                <div className="bulk-table-responsive">
                                    <table className="bulk-table">
                                        <thead>
                                            <tr>
                                                <th>#</th>
                                                <th>Status</th>
                                                <th>Ship Number</th>
                                                <th>Account</th>
                                                <th>Recipient / Destination</th>
                                                <th>Weight</th>
                                                <th>Product / HS Code</th>
                                                <th>Tracking #</th>
                                                <th>Actions</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {displayedRows.map((row) => (
                                                <tr key={row.id}>
                                                    <td><strong>{row.rowIndex}</strong></td>
                                                    <td>
                                                        {row.status === 'ready' && (
                                                            <span className="bulk-badge bulk-badge-pending">⏳ Ready</span>
                                                        )}
                                                        {row.status === 'processing' && (
                                                            <span className="bulk-badge bulk-badge-processing">
                                                                <div className="bulk-spinner bulk-spinner-dark" style={{ width: '10px', height: '10px' }}></div>
                                                                Processing
                                                            </span>
                                                        )}
                                                        {row.status === 'success' && (
                                                            <span className="bulk-badge bulk-badge-success">✅ Created</span>
                                                        )}
                                                        {(row.status === 'error' || row.status === 'failed') && (
                                                            <button
                                                                type="button"
                                                                className="bulk-fix-badge-btn"
                                                                onClick={() => {
                                                                    setEditRowModal({ row, index: row.rowIndex, data: { ...row.data } });
                                                                    setEditActiveSection('all');
                                                                    setEditSearchQuery('');
                                                                }}
                                                                title={row.errorMessage || row.errors.join('; ')}
                                                            >
                                                                ❌ {row.status === 'failed' ? 'Failed' : 'Invalid'} • Fix (✏️)
                                                            </button>
                                                        )}
                                                    </td>
                                                    <td><code>{row.shipNumber}</code></td>
                                                    <td>
                                                        <span style={{ fontWeight: 600, color: '#334155' }}>
                                                            {row.data.effectiveAccount || defaultAccount || 'None'}
                                                        </span>
                                                    </td>
                                                    <td>
                                                        <div style={{ fontWeight: 600, color: '#0f172a' }}>
                                                            {row.data.ContactName || row.data.Companyname || 'N/A'}
                                                        </div>
                                                        <div style={{ fontSize: '11px', color: '#64748b' }}>
                                                            {row.data.City}, {row.data.StateProvOther ? `${row.data.StateProvOther}, ` : ''}{row.data.Country} ({row.data.PostalCode})
                                                        </div>
                                                    </td>
                                                    <td>{row.data.Weight || '0'} {row.data.UnitofMeasure || 'KGS'}</td>
                                                    <td>
                                                        <div style={{ maxWidth: '200px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                                            {row.data.DescriptionofGoods || row.data.productdisctription || 'Merchandise'}
                                                        </div>
                                                        <div style={{ fontSize: '11px', color: '#64748b' }}>
                                                            HS: {row.data.TariffCode || row.data.producthscode || 'N/A'} • {row.data.NumberOfUnits || row.data.productquantity || 1} units
                                                        </div>
                                                    </td>
                                                    <td>
                                                        {row.trackingNumber ? (
                                                            <a
                                                                href={`https://www.ups.com/track?loc=en_US&tracknum=${row.trackingNumber}`}
                                                                target="_blank"
                                                                rel="noopener noreferrer"
                                                                className="bulk-tracking-link"
                                                            >
                                                                {row.trackingNumber}
                                                                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6M15 3h6v6M10 14L21 3" /></svg>
                                                            </a>
                                                        ) : (
                                                            <span style={{ color: '#94a3b8' }}>—</span>
                                                        )}
                                                    </td>
                                                    <td>
                                                        <div className="bulk-row-actions">
                                                            {row.status === 'success' && (
                                                                <>
                                                                    <button
                                                                        className="bulk-icon-btn"
                                                                        onClick={() => handleViewSingleLabel(row)}
                                                                        title="View & Print UPS Label"
                                                                    >
                                                                        🏷️
                                                                    </button>
                                                                    <button
                                                                        className="bulk-icon-btn"
                                                                        onClick={() => handleViewSingleInvoice(row)}
                                                                        title="View & Print Commercial Invoice"
                                                                    >
                                                                        📄
                                                                    </button>
                                                                </>
                                                            )}
                                                            <button
                                                                className="bulk-icon-btn"
                                                                onClick={() => {
                                                                    setEditRowModal({ row, index: row.rowIndex, data: { ...row.data } });
                                                                    setEditActiveSection('all');
                                                                    setEditSearchQuery('');
                                                                }}
                                                                title="Inspect / Edit All 57 Row Fields"
                                                                style={(row.status === 'error' || row.status === 'failed') ? { borderColor: '#ef4444', color: '#dc2626', background: '#fee2e2' } : {}}
                                                            >
                                                                ✏️
                                                            </button>
                                                        </div>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        )}
                    </>
                )}

                {/* ── Past Batches / History Tab ── */}
                {activeTab === 'history' && (
                    <div className="bulk-guide-card">
                        <div className="bulk-guide-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <div>
                                <h2>Recent Bulk Shipment History</h2>
                                <p>Past shipments generated via the Bulk Shipment module</p>
                            </div>
                            <button className="bulk-btn bulk-btn-secondary" onClick={fetchHistory} disabled={loadingHistory}>
                                🔄 Refresh History
                            </button>
                        </div>

                        {loadingHistory ? (
                            <div style={{ textAlign: 'center', padding: '40px', color: '#64748b' }}>
                                Loading past shipments...
                            </div>
                        ) : historyList.length === 0 ? (
                            <div style={{ textAlign: 'center', padding: '40px', color: '#64748b' }}>
                                No past bulk shipments found. Once you generate shipments from the Upload tab, they will appear here.
                            </div>
                        ) : (
                            <div className="bulk-table-container">
                                <div className="bulk-table-responsive">
                                    <table className="bulk-table">
                                        <thead>
                                            <tr>
                                                <th>Date</th>
                                                <th>Tracking #</th>
                                                <th>Shipment #</th>
                                                <th>Account</th>
                                                <th>Recipient</th>
                                                <th>Country</th>
                                                <th>Charges</th>
                                                <th>Actions</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {historyList.map((item, idx) => (
                                                <tr key={item.trackingNumber || idx}>
                                                    <td>{item.shipmentDate ? new Date(item.shipmentDate).toLocaleString() : 'N/A'}</td>
                                                    <td>
                                                        <a
                                                            href={`https://www.ups.com/track?loc=en_US&tracknum=${item.trackingNumber}`}
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                            className="bulk-tracking-link"
                                                        >
                                                            {item.trackingNumber}
                                                        </a>
                                                    </td>
                                                    <td>{item.shipmentNumber || '—'}</td>
                                                    <td>{item.accountNumber || '—'}</td>
                                                    <td>{item.destination?.name || item.destination?.company || 'N/A'}</td>
                                                    <td><strong>{item.destination?.country || '—'}</strong></td>
                                                    <td>{item.totalCharges ? `$${item.totalCharges} ${item.currency || 'USD'}` : '—'}</td>
                                                    <td>
                                                        <div className="bulk-row-actions">
                                                            {item.labels && item.labels.length > 0 && (
                                                                <button
                                                                    className="bulk-icon-btn"
                                                                    onClick={() => setLabelModalData(item.labels)}
                                                                    title="Print Label"
                                                                >
                                                                    🏷️
                                                                </button>
                                                            )}
                                                            <button
                                                                className="bulk-icon-btn"
                                                                onClick={() => {
                                                                    const ciData = {
                                                                        shipper: item.shipper || {},
                                                                        destination: item.destination || {},
                                                                        products: item.products || [],
                                                                        invoiceNo: item.invoiceNo || '',
                                                                        commodityInvoiceNo: item.commodityInvoiceNo || '',
                                                                        exportReason: item.exportReason || 'GIFT',
                                                                        countryOfOrigin: item.countryOfOrigin || 'PK',
                                                                        currencyType: item.currencyType || item.currency || 'USD',
                                                                        trackingNumber: item.trackingNumber,
                                                                        date: new Date(item.shipmentDate).toLocaleDateString('en-GB')
                                                                    };
                                                                    const html = generateCommercialInvoiceHTML(ciData);
                                                                    setCiModalData({ html, tracking: item.trackingNumber, rawPdf: item.upsCommercialInvoice });
                                                                }}
                                                                title="Commercial Invoice"
                                                            >
                                                                📄
                                                            </button>
                                                        </div>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        )}
                    </div>
                )}

                {/* ── Format Guide Tab ── */}
                {activeTab === 'guide' && (
                    <div className="bulk-guide-card">
                        <div className="bulk-guide-header">
                            <h2>Excel Field Guide & Reference</h2>
                            <p>All supported columns for the bulk shipment Excel import file</p>
                        </div>

                        <div className="bulk-guide-grid">
                            <div className="bulk-guide-section">
                                <h4>🏢 Shipper Information</h4>
                                <ul className="bulk-guide-list">
                                    <li><span className="bulk-guide-col-name">AccountNO <span className="bulk-guide-required">*</span></span> <span className="bulk-guide-col-desc">UPS Account to charge</span></li>
                                    <li><span className="bulk-guide-col-name">Shippercontactname <span className="bulk-guide-required">*</span></span> <span className="bulk-guide-col-desc">Sender contact person</span></li>
                                    <li><span className="bulk-guide-col-name">shippercompany <span className="bulk-guide-required">*</span></span> <span className="bulk-guide-col-desc">Sender company name</span></li>
                                    <li><span className="bulk-guide-col-name">Shippercountry <span className="bulk-guide-required">*</span></span> <span className="bulk-guide-col-desc">Origin Country (e.g. PK)</span></li>
                                    <li><span className="bulk-guide-col-name">ShipperAddressone <span className="bulk-guide-required">*</span></span> <span className="bulk-guide-col-desc">Street address 1</span></li>
                                    <li><span className="bulk-guide-col-name">ShipperCity <span className="bulk-guide-required">*</span></span> <span className="bulk-guide-col-desc">Sender city</span></li>
                                    <li><span className="bulk-guide-col-name">ShipperTELE <span className="bulk-guide-required">*</span></span> <span className="bulk-guide-col-desc">Sender telephone</span></li>
                                    <li><span className="bulk-guide-col-name">ShipperEmail</span> <span className="bulk-guide-col-desc">Sender email</span></li>
                                    <li><span className="bulk-guide-col-name">ShipperTaxID</span> <span className="bulk-guide-col-desc">Tax ID / NTN</span></li>
                                </ul>
                            </div>

                            <div className="bulk-guide-section">
                                <h4>📍 Recipient (Consignee)</h4>
                                <ul className="bulk-guide-list">
                                    <li><span className="bulk-guide-col-name">ContactName <span className="bulk-guide-required">*</span></span> <span className="bulk-guide-col-desc">Recipient person name</span></li>
                                    <li><span className="bulk-guide-col-name">Companyname</span> <span className="bulk-guide-col-desc">Recipient company</span></li>
                                    <li><span className="bulk-guide-col-name">Country <span className="bulk-guide-required">*</span></span> <span className="bulk-guide-col-desc">2-letter ISO (US, GB, AE)</span></li>
                                    <li><span className="bulk-guide-col-name">Addressone <span className="bulk-guide-required">*</span></span> <span className="bulk-guide-col-desc">Delivery street address</span></li>
                                    <li><span className="bulk-guide-col-name">City <span className="bulk-guide-required">*</span></span> <span className="bulk-guide-col-desc">Destination city</span></li>
                                    <li><span className="bulk-guide-col-name">StateProvOther</span> <span className="bulk-guide-col-desc">Required for US/CA (e.g. NY)</span></li>
                                    <li><span className="bulk-guide-col-name">PostalCode <span className="bulk-guide-required">*</span></span> <span className="bulk-guide-col-desc">ZIP / Postal code</span></li>
                                    <li><span className="bulk-guide-col-name">Telephone <span className="bulk-guide-required">*</span></span> <span className="bulk-guide-col-desc">Recipient phone</span></li>
                                    <li><span className="bulk-guide-col-name">ResidentialInd</span> <span className="bulk-guide-col-desc">Y = Home, N = Commercial</span></li>
                                </ul>
                            </div>

                            <div className="bulk-guide-section">
                                <h4>📦 Package & Specifications</h4>
                                <ul className="bulk-guide-list">
                                    <li><span className="bulk-guide-col-name">PackagingType</span> <span className="bulk-guide-col-desc">02=Box, 01=Letter, 24=25kg, 25=10kg</span></li>
                                    <li><span className="bulk-guide-col-name">Weight <span className="bulk-guide-required">*</span></span> <span className="bulk-guide-col-desc">Weight (e.g. 2.5)</span></li>
                                    <li><span className="bulk-guide-col-name">UnitofMeasure</span> <span className="bulk-guide-col-desc">KGS or LBS</span></li>
                                    <li><span className="bulk-guide-col-name">Length, Width, Height</span> <span className="bulk-guide-col-desc">Dimensions (CM/IN)</span></li>
                                    <li><span className="bulk-guide-col-name">CustomsValue</span> <span className="bulk-guide-col-desc">Declared customs value</span></li>
                                    <li><span className="bulk-guide-col-name">Service</span> <span className="bulk-guide-col-desc">65 = UPS Saver, 07 = Express</span></li>
                                    <li><span className="bulk-guide-col-name">SaturdayDeliver</span> <span className="bulk-guide-col-desc">Y / N</span></li>
                                    <li><span className="bulk-guide-col-name">Addlhandling</span> <span className="bulk-guide-col-desc">Y = Additional Handling</span></li>
                                </ul>
                            </div>

                            <div className="bulk-guide-section">
                                <h4>📑 Commercial Invoice Line Items</h4>
                                <ul className="bulk-guide-list">
                                    <li><span className="bulk-guide-col-name">DescriptionofGoods <span className="bulk-guide-required">*</span></span> <span className="bulk-guide-col-desc">Detailed item description</span></li>
                                    <li><span className="bulk-guide-col-name">TariffCode <span className="bulk-guide-required">*</span></span> <span className="bulk-guide-col-desc">6-8 digit HS tariff code</span></li>
                                    <li><span className="bulk-guide-col-name">NumberOfUnits <span className="bulk-guide-required">*</span></span> <span className="bulk-guide-col-desc">Number of units</span></li>
                                    <li><span className="bulk-guide-col-name">UnitPrice <span className="bulk-guide-required">*</span></span> <span className="bulk-guide-col-desc">Price per unit (USD)</span></li>
                                    <li><span className="bulk-guide-col-name">OriginCountry <span className="bulk-guide-required">*</span></span> <span className="bulk-guide-col-desc">Country made (e.g. PK)</span></li>
                                    <li><span className="bulk-guide-col-name">PartNumber / UPC / ASIN</span> <span className="bulk-guide-col-desc">SKU or Merchant code</span></li>
                                    <li><span className="bulk-guide-col-name">DocumentsofNoCommercialValue</span> <span className="bulk-guide-col-desc">Y = Document (no invoice)</span></li>
                                </ul>
                            </div>
                        </div>
                    </div>
                )}
            </div>

            {/* ── Label Modal ── */}
            {labelModalData && (
                <div className="bulk-modal-backdrop" onClick={() => setLabelModalData(null)}>
                    <div className="bulk-modal" onClick={e => e.stopPropagation()}>
                        <div className="bulk-modal-header">
                            <h3>🏷️ Official UPS Shipping Label</h3>
                            <button className="bulk-modal-close" onClick={() => setLabelModalData(null)}>×</button>
                        </div>
                        <div className="bulk-modal-body" style={{ textAlign: 'center' }}>
                            {labelModalData.map((lbl, idx) => (
                                <div key={idx} style={{ marginBottom: '20px' }}>
                                    <img
                                        src={`data:image/gif;base64,${lbl}`}
                                        alt="UPS Label"
                                        style={{ maxWidth: '100%', maxHeight: '600px', border: '1px solid #cbd5e1', borderRadius: '8px' }}
                                    />
                                </div>
                            ))}
                        </div>
                        <div className="bulk-modal-footer">
                            <button
                                className="bulk-btn bulk-btn-primary"
                                onClick={() => {
                                    const win = window.open('');
                                    win.document.write(`<html><head><title>Print Label</title><style>@page{size:A4;margin:10mm;}body{margin:0;display:flex;justify-content:center;align-items:center;min-height:90vh;}</style></head><body><img src="data:image/gif;base64,${labelModalData[0]}"/></body></html>`);
                                    win.document.close();
                                    win.print();
                                }}
                            >
                                🖨️ Print Label
                            </button>
                            <button
                                className="bulk-btn bulk-btn-secondary"
                                onClick={() => {
                                    const a = document.createElement('a');
                                    a.href = `data:image/gif;base64,${labelModalData[0]}`;
                                    a.download = `ups_label_${Date.now()}.gif`;
                                    a.click();
                                }}
                            >
                                📥 Download
                            </button>
                            <button className="bulk-btn bulk-btn-secondary" onClick={() => setLabelModalData(null)}>
                                Close
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* ── Commercial Invoice Modal ── */}
            {ciModalData && (
                <div className="bulk-modal-backdrop" onClick={() => setCiModalData(null)}>
                    <div className="bulk-modal" style={{ maxWidth: '900px' }} onClick={e => e.stopPropagation()}>
                        <div className="bulk-modal-header">
                            <h3>📄 Commercial Invoice — {ciModalData.tracking}</h3>
                            <button className="bulk-modal-close" onClick={() => setCiModalData(null)}>×</button>
                        </div>
                        <div className="bulk-modal-body" style={{ background: '#f8fafc', padding: '16px' }}>
                            <div
                                style={{ background: 'white', padding: '20px', borderRadius: '8px', boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}
                                dangerouslySetInnerHTML={{ __html: ciModalData.html }}
                            />
                        </div>
                        <div className="bulk-modal-footer">
                            <button
                                className="bulk-btn bulk-btn-primary"
                                onClick={() => {
                                    const win = window.open('', '_blank');
                                    win.document.write(`<!DOCTYPE html><html><head><meta charset="utf-8"><title>Commercial Invoice - ${ciModalData.tracking}</title><style>@page{size:A4 portrait;margin:0;}body{margin:0;padding:10px;}</style></head><body>${ciModalData.html}</body></html>`);
                                    win.document.close();
                                    win.print();
                                }}
                            >
                                🖨️ Print Invoice
                            </button>
                            {ciModalData.rawPdf && (
                                <button
                                    className="bulk-btn bulk-btn-purple"
                                    onClick={() => {
                                        const isPdf = ciModalData.rawPdf.startsWith('JVBERi');
                                        const mime = isPdf ? 'application/pdf' : 'image/png';
                                        const win = window.open();
                                        if (win) {
                                            win.document.write(`<iframe width="100%" height="100%" style="border:none;" src="data:${mime};base64,${ciModalData.rawPdf}"></iframe>`);
                                            win.document.close();
                                        }
                                    }}
                                >
                                    📄 UPS Electronic PDF
                                </button>
                            )}
                            <button className="bulk-btn bulk-btn-secondary" onClick={() => setCiModalData(null)}>
                                Close
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* ── Edit / Inspect Row Modal (All 57 Fields) ── */}
            {editRowModal && (() => {
                const formData = editRowModal.data || {};
                const liveVal = validateRow(formData, accountsList, defaultAccount);
                const liveErrors = liveVal.errors || [];
                const isLiveValid = liveErrors.length === 0;

                const getFieldError = (key) => {
                    if (key === 'AccountNO') {
                        const acc = String(formData.effectiveAccount || formData.AccountNO || '').trim();
                        if (!acc || acc.toLowerCase() === 'all') return 'UPS Account number is missing';
                        if (!hasAllAccess && accountsList.length > 0 && !accountsList.map(a => String(a).toUpperCase()).includes(acc.toUpperCase())) {
                            return `Account ${acc} is not authorized for your user`;
                        }
                    }
                    if (key === 'Country' && !String(formData.Country || '').trim()) return 'Destination Country is required';
                    if (key === 'Addressone' && !String(formData.Addressone || '').trim()) return 'Destination Address Line 1 is required';
                    if (key === 'City' && !String(formData.City || '').trim()) return 'Destination City is required';
                    if (key === 'ContactName' && !String(formData.ContactName || '').trim() && !String(formData.Companyname || '').trim()) {
                        return 'Recipient Contact Name or Company is required';
                    }
                    if (key === 'Weight' && (!formData.Weight || parseFloat(formData.Weight) <= 0)) {
                        return 'Weight must be greater than 0';
                    }
                    if (key === 'StateProvOther') {
                        const country = String(formData.Country || '').trim().toUpperCase();
                        if (['US', 'CA'].includes(country)) {
                            const st = String(formData.StateProvOther || '').trim();
                            if (!st || st.length !== 2) return `2-letter state code required for ${country}`;
                        }
                    }
                    return null;
                };

                const updateField = (key, value) => {
                    const next = { ...formData, [key]: value };
                    if (key === 'AccountNO') {
                        next.effectiveAccount = value;
                    }
                    if (key === 'Country') {
                        next.Country = String(value).toUpperCase();
                    }
                    if (key === 'StateProvOther') {
                        next.StateProvOther = String(value).toUpperCase();
                    }
                    setEditRowModal({ ...editRowModal, data: next });
                };

                const getSectionErrorCount = (sec) => {
                    return sec.fields.filter(f => !!getFieldError(f.key)).length;
                };

                const totalErrorsCount = EDIT_SECTIONS.reduce((sum, s) => sum + getSectionErrorCount(s), 0);

                const filteredSections = EDIT_SECTIONS.map(sec => {
                    let fields = sec.fields;
                    if (editSearchQuery.trim()) {
                        const q = editSearchQuery.trim().toLowerCase();
                        fields = fields.filter(f =>
                            f.key.toLowerCase().includes(q) ||
                            f.label.toLowerCase().includes(q) ||
                            String(formData[f.key] || '').toLowerCase().includes(q)
                        );
                    }
                    return { ...sec, fields };
                }).filter(sec => {
                    if (editActiveSection !== 'all' && sec.id !== editActiveSection) return false;
                    return sec.fields.length > 0;
                });

                return (
                    <div className="bulk-modal-backdrop" onClick={() => setEditRowModal(null)}>
                        <div className="bulk-modal bulk-modal-large" onClick={e => e.stopPropagation()}>
                            <div className="bulk-modal-header">
                                <div>
                                    <h3>✏️ Edit Shipment #{editRowModal.index} — {formData.ShipNumber || editRowModal.row.shipNumber}</h3>
                                    <p style={{ margin: '3px 0 0', fontSize: '12px', color: '#64748b' }}>
                                        Inspect and fix all 57 shipment fields. Changes re-validate live in real time.
                                    </p>
                                </div>
                                <button className="bulk-modal-close" onClick={() => setEditRowModal(null)}>×</button>
                            </div>

                            <div className="bulk-modal-body">
                                {liveErrors.length > 0 && (
                                    <div style={{ background: '#fee2e2', border: '1px solid #fecaca', borderRadius: '10px', padding: '12px 16px', marginBottom: '16px', color: '#991b1b', fontSize: '13px' }}>
                                        <div style={{ fontWeight: 700, display: 'flex', alignItems: 'center', gap: '6px' }}>
                                            <span>⚠️</span> {liveErrors.length} Validation Issue{liveErrors.length > 1 ? 's' : ''} Found (Fix highlighted fields below):
                                        </div>
                                        <ul style={{ margin: '6px 0 0 18px', padding: 0 }}>
                                            {liveErrors.map((err, i) => (
                                                <li key={i}>{err}</li>
                                            ))}
                                        </ul>
                                    </div>
                                )}

                                {editRowModal.row.errorMessage && (
                                    <div style={{ background: '#fef2f2', border: '1px solid #f87171', borderRadius: '10px', padding: '12px 16px', marginBottom: '16px', color: '#991b1b', fontSize: '13px' }}>
                                        <strong>🚨 UPS API Execution Error:</strong> {editRowModal.row.errorMessage}
                                    </div>
                                )}

                                <div className="bulk-edit-nav-bar">
                                    <div className="bulk-edit-search-box">
                                        <span className="bulk-edit-search-icon">🔍</span>
                                        <input
                                            type="text"
                                            className="bulk-edit-search-input"
                                            placeholder="Search any of the 57 fields (e.g. State, Weight, Tariff, Tax)..."
                                            value={editSearchQuery}
                                            onChange={e => setEditSearchQuery(e.target.value)}
                                        />
                                    </div>

                                    <div className="bulk-edit-tab-pills">
                                        <button
                                            type="button"
                                            className={`bulk-edit-pill ${editActiveSection === 'all' ? 'active' : ''}`}
                                            onClick={() => setEditActiveSection('all')}
                                        >
                                            📋 All Fields <span className="bulk-edit-pill-badge">57</span>
                                            {totalErrorsCount > 0 && <span className="bulk-edit-pill-error-dot"></span>}
                                        </button>

                                        {EDIT_SECTIONS.map(sec => {
                                            const errCount = getSectionErrorCount(sec);
                                            return (
                                                <button
                                                    key={sec.id}
                                                    type="button"
                                                    className={`bulk-edit-pill ${editActiveSection === sec.id ? 'active' : ''}`}
                                                    onClick={() => setEditActiveSection(sec.id)}
                                                >
                                                    <span>{sec.icon}</span> {sec.title.split(' ')[0]}
                                                    <span className="bulk-edit-pill-badge">{sec.fields.length}</span>
                                                    {errCount > 0 && <span className="bulk-edit-pill-error-dot" title={`${errCount} issue(s) in this section`}></span>}
                                                </button>
                                            );
                                        })}
                                    </div>
                                </div>

                                {filteredSections.length === 0 ? (
                                    <div style={{ textAlign: 'center', padding: '30px', color: '#94a3b8' }}>
                                        No fields matched "<strong>{editSearchQuery}</strong>". Try clearing your search query.
                                    </div>
                                ) : (
                                    filteredSections.map(sec => (
                                        <div key={sec.id} className="bulk-edit-section-card">
                                            <div className="bulk-edit-section-header">
                                                <h4>
                                                    <span>{sec.icon}</span> {sec.title}
                                                </h4>
                                                <span style={{ fontSize: '11px', color: '#64748b' }}>
                                                    {sec.fields.length} field{sec.fields.length > 1 ? 's' : ''}
                                                </span>
                                            </div>
                                            <div className="bulk-edit-fields-grid">
                                                {sec.fields.map(f => {
                                                    const err = getFieldError(f.key);
                                                    const val = formData[f.key] ?? '';

                                                    return (
                                                        <div key={f.key} className="bulk-field-group">
                                                            <label className="bulk-field-label">
                                                                <span>
                                                                    {f.label} {f.required && <span className="bulk-req-star">*</span>}
                                                                </span>
                                                                <span className="bulk-field-code">{f.key}</span>
                                                            </label>

                                                            {f.key === 'AccountNO' ? (
                                                                <div>
                                                                    <select
                                                                        className={`bulk-field-input ${err ? 'has-error' : ''}`}
                                                                        value={formData.effectiveAccount || formData.AccountNO || ''}
                                                                        onChange={e => updateField('AccountNO', e.target.value)}
                                                                    >
                                                                        <option value="">-- Select UPS Account --</option>
                                                                        {accountsList.map(acc => (
                                                                            <option key={acc} value={acc}>
                                                                                {acc} {companySlug ? '(Company Account)' : '(Assigned)'}
                                                                            </option>
                                                                        ))}
                                                                    </select>
                                                                </div>
                                                            ) : f.type === 'select' ? (
                                                                <select
                                                                    className={`bulk-field-input ${err ? 'has-error' : ''}`}
                                                                    value={val}
                                                                    onChange={e => updateField(f.key, e.target.value)}
                                                                >
                                                                    {f.options.map(opt => (
                                                                        <option key={opt.value} value={opt.value}>
                                                                            {opt.label}
                                                                        </option>
                                                                    ))}
                                                                </select>
                                                            ) : (
                                                                <input
                                                                    type={f.type || 'text'}
                                                                    step={f.step}
                                                                    className={`bulk-field-input ${err ? 'has-error' : ''}`}
                                                                    placeholder={f.placeholder || f.label}
                                                                    value={val}
                                                                    onChange={e => updateField(f.key, e.target.value)}
                                                                />
                                                            )}

                                                            {err && (
                                                                <div className="bulk-field-error-msg">
                                                                    <span>⚠️</span> {err}
                                                                </div>
                                                            )}
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        </div>
                                    ))
                                )}
                            </div>

                            <div className="bulk-modal-footer" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <div>
                                    {isLiveValid ? (
                                        <span style={{ fontSize: '13px', color: '#16a34a', fontWeight: 600, display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
                                            ✅ All fields valid! Ready to generate UPS label & invoice.
                                        </span>
                                    ) : (
                                        <span style={{ fontSize: '13px', color: '#dc2626', fontWeight: 600, display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
                                            ⚠️ {liveErrors.length} issue{liveErrors.length > 1 ? 's' : ''} remaining. Please fix highlighted fields above.
                                        </span>
                                    )}
                                </div>
                                <div style={{ display: 'flex', gap: '10px' }}>
                                    <button
                                        type="button"
                                        className="bulk-btn bulk-btn-secondary"
                                        onClick={() => setEditRowModal(null)}
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        type="button"
                                        className="bulk-btn bulk-btn-primary"
                                        onClick={() => {
                                            const v = validateRow(formData, accountsList, defaultAccount);
                                            setParsedRows(prev => prev.map(r => {
                                                if (r.id === editRowModal.row.id) {
                                                    return {
                                                        ...r,
                                                        shipNumber: formData.ShipNumber || r.shipNumber,
                                                        data: v.rowObj,
                                                        errors: v.errors,
                                                        errorMessage: null,
                                                        status: v.errors.length === 0 ? 'ready' : 'error'
                                                    };
                                                }
                                                return r;
                                            }));
                                            setEditRowModal(null);
                                        }}
                                    >
                                        💾 Save Changes & Re-validate
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                );
            })()}
        </div>
    );
};

export default BulkShipping;
